import { useState, type ReactNode } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { useJobs } from '../lib/queries';
import { useTheme } from '../lib/ThemeProvider';
import { cn } from '../lib/utils';
import type { UserRole } from '../lib/database.types';

interface NavItem {
    to: string;
    label: string;
    roles: UserRole[];
    badge?: number;
}

export function AppShell({ children }: { children: ReactNode }) {
    const { profile, role, signOut } = useAuth();
    const { theme, toggleTheme } = useTheme();
    const navigate = useNavigate();
    const [menuOpen, setMenuOpen] = useState(false);
    const [userMenuOpen, setUserMenuOpen] = useState(false);

    // Show a review badge on the nav for admins
    const { data: reviewJobs } = useJobs({ needsReview: true });
    const reviewCount = reviewJobs?.length ?? 0;

    // Dispatch came off the nav on 3 Sep. Kristine, 2 Sep: "Can you remove this
    // tab. We were going to use this for Service and don't need it anymore."
    // The route and the page are left in place, so nothing breaks for anyone
    // holding a link, it is just not offered any more. The badge query went with
    // it rather than polling every 30 seconds for a count nobody sees.

    const navItems: NavItem[] = [
        { to: '/board', label: 'Board', roles: ['admin', 'viewer'] },
        { to: '/field', label: 'Field', roles: ['admin', 'field'] },
        { to: '/review', label: 'Review', roles: ['admin'], badge: reviewCount },
        { to: '/service', label: 'Service', roles: ['admin'] },
        { to: '/reports', label: 'Reports', roles: ['admin'] },
        { to: '/weather', label: 'Weather', roles: ['admin'] },
        { to: '/activity', label: 'Activity', roles: ['admin', 'field'] },
        // Phase 10. Last in the nav on purpose: it is the screen you visit when
        // something needs setting up, not one you work out of day to day.
        { to: '/admin', label: 'Records', roles: ['admin'] },
    ];

    const visibleNav = navItems.filter((item) => role && item.roles.includes(role));

    async function handleSignOut() {
        await signOut();
        navigate('/');
    }

    return (
        <div className="min-h-screen flex flex-col bg-graphite-50 dark:bg-graphite-950">
            <header className="sticky top-0 z-30 bg-graphite-50 dark:bg-graphite-950 border-b border-graphite-200 dark:border-graphite-800">
                <div className="flex items-center h-14 px-4 md:px-6 gap-4">
                    {/* Mark */}
                    <div className="flex items-baseline gap-2.5 min-w-0">
                        <span className="font-display font-bold text-graphite-900 dark:text-graphite-50 text-lg tracking-tight">
                            PPC
                        </span>
                        <span className="hidden sm:inline font-mono text-[10px] uppercase tracking-[0.2em] text-graphite-500">
                            Scheduling
                        </span>
                    </div>

                    {/* Desktop nav */}
                    <nav className="hidden md:flex items-center gap-1 ml-4">
                        {visibleNav.map((item) => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                className={({ isActive }) =>
                                    cn(
                                        'relative font-mono text-xs uppercase tracking-widest px-3 py-1.5 rounded transition-colors',
                                        isActive
                                            ? 'text-graphite-900 bg-graphite-200 dark:text-graphite-50 dark:bg-graphite-800'
                                            : 'text-graphite-600 hover:text-graphite-900 dark:text-graphite-500 dark:hover:text-graphite-200'
                                    )
                                }
                            >
                                {item.label}
                                {item.badge && item.badge > 0 ? (
                                    <span className="ml-1.5 inline-flex items-center justify-center h-4 min-w-4 px-1 bg-amber-500 text-graphite-950 rounded text-[10px] font-bold">
                                        {item.badge}
                                    </span>
                                ) : null}
                            </NavLink>
                        ))}
                    </nav>

                    <div className="flex-1" />

                    {/* Theme toggle */}
                    <button
                        type="button"
                        onClick={toggleTheme}
                        aria-label={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
                        title={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
                        className="h-8 w-8 flex items-center justify-center rounded text-graphite-600 hover:text-graphite-900 hover:bg-graphite-100 dark:text-graphite-400 dark:hover:text-graphite-100 dark:hover:bg-graphite-800 transition-colors"
                    >
                        {theme === 'light' ? (
                            // Moon icon (clicking moves to dark mode)
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                                <path
                                    d="M13 8.5A5.5 5.5 0 1 1 7.5 3a4 4 0 0 0 5.5 5.5z"
                                    fill="currentColor"
                                />
                            </svg>
                        ) : (
                            // Sun icon (clicking moves to light mode)
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                                <circle cx="8" cy="8" r="3" fill="currentColor" />
                                <path
                                    d="M8 1v2M8 13v2M3.05 3.05l1.41 1.41M11.54 11.54l1.41 1.41M1 8h2M13 8h2M3.05 12.95l1.41-1.41M11.54 4.46l1.41-1.41"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                />
                            </svg>
                        )}
                    </button>

                    {/* User */}
                    <div className="relative">
                        <button
                            type="button"
                            onClick={() => setUserMenuOpen((v) => !v)}
                            className="flex items-center gap-2.5 font-mono text-xs text-graphite-600 hover:text-graphite-900 dark:text-graphite-400 dark:hover:text-graphite-100 transition-colors"
                        >
                            <span className="hidden sm:flex flex-col items-end leading-tight">
                                <span className="text-graphite-900 dark:text-graphite-100 text-sm font-sans font-medium">
                                    {profile?.full_name ?? '—'}
                                </span>
                                <span className="uppercase tracking-widest text-[9px] text-graphite-500">
                                    {role}
                                </span>
                            </span>
                            <span className="h-8 w-8 bg-graphite-200 border border-graphite-300 dark:bg-graphite-800 dark:border-graphite-700 rounded flex items-center justify-center text-graphite-700 dark:text-graphite-300 font-semibold text-xs">
                                {(profile?.full_name ?? '?').charAt(0).toUpperCase()}
                            </span>
                        </button>
                        {userMenuOpen ? (
                            <>
                                <button
                                    type="button"
                                    aria-label="Close menu"
                                    className="fixed inset-0 z-40"
                                    onClick={() => setUserMenuOpen(false)}
                                />
                                <div className="absolute right-0 mt-2 w-52 bg-white border border-graphite-200 dark:bg-graphite-900 dark:border-graphite-700 rounded shadow-card-md py-1 z-50">
                                    <button
                                        type="button"
                                        onClick={handleSignOut}
                                        className="w-full text-left px-3 py-2 text-sm text-graphite-800 hover:bg-graphite-100 dark:text-graphite-200 dark:hover:bg-graphite-800 transition-colors"
                                    >
                                        Sign out
                                    </button>
                                </div>
                            </>
                        ) : null}
                    </div>

                    {/* Mobile menu button */}
                    <button
                        type="button"
                        className="md:hidden text-graphite-700 hover:text-graphite-900 dark:text-graphite-300 dark:hover:text-graphite-50 -mr-1 p-1"
                        onClick={() => setMenuOpen((v) => !v)}
                        aria-label="Toggle menu"
                    >
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path d="M3 6h14M3 10h14M3 14h14" stroke="currentColor" strokeWidth="1.75" />
                        </svg>
                    </button>
                </div>

                {/* Mobile nav drawer */}
                {menuOpen ? (
                    <nav className="md:hidden border-t border-graphite-200 dark:border-graphite-800 px-2 py-2 flex flex-col">
                        {visibleNav.map((item) => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                onClick={() => setMenuOpen(false)}
                                className={({ isActive }) =>
                                    cn(
                                        'font-mono text-xs uppercase tracking-widest px-3 py-2.5 rounded',
                                        isActive
                                            ? 'text-graphite-900 bg-graphite-200 dark:text-graphite-50 dark:bg-graphite-800'
                                            : 'text-graphite-600 hover:text-graphite-900 dark:text-graphite-400 dark:hover:text-graphite-100'
                                    )
                                }
                            >
                                <span className="flex items-center justify-between">
                                    {item.label}
                                    {item.badge && item.badge > 0 ? (
                                        <span className="inline-flex items-center justify-center h-4 min-w-4 px-1 bg-amber-500 text-graphite-950 rounded text-[10px] font-bold">
                                            {item.badge}
                                        </span>
                                    ) : null}
                                </span>
                            </NavLink>
                        ))}
                    </nav>
                ) : null}
            </header>

            <main className="flex-1 min-h-0">{children}</main>
        </div>
    );
}
